import { CommonModule } from '@angular/common';
import { Component, Inject, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ServiceHandler } from '@nucleus/ui-base';
import { PageHeaderComponent } from "@nucleus/ui-base";
import { UserDataTypeService } from '../../services/userdata-type/userdata-type.service';
import { UserDataTypeReadOnlyStore } from '../../services/userdata-type/userdata-type-readonly.store';
import { PermissionCheckerService } from '../../services/permission/permission-checker.service';
import {
    UserDataTypeAttribute,
    UserDataTypeAttributeCreateRequest,
    UserDataTypeAttributeUpdateRequest,
    UserDataValueType,
} from '../../services/userdata-type/userdata-type.models';

export interface UserDataTypeAttributeDialogData {
    /** Owning type (NE, PROBE, …). */
    type: string;
    /** Provided when editing; null when creating. */
    attribute: UserDataTypeAttribute | null;
}

/**
 * Create-or-edit dialog for a user_data_type_attribute rule. Uses the
 * canonical bw-dialog shell with <lib-page-header> for the title bar
 * (per feedback_dialog_use_app_page_header).
 *
 * On create: name + payloadPath + valueType + indexed + description
 *            POST as source=MANUAL.
 * On edit:   only valueType + indexed + description are mutable; name and
 *            payloadPath are the rule's identity and are read-only.
 */
@Component({
    selector: 'userdata-type-attribute-dialog',
    standalone: true,
    imports: [
        CommonModule, FormsModule, ReactiveFormsModule,
        MatDialogModule, MatFormFieldModule, MatInputModule,
        MatButtonModule, MatIconModule, MatSelectModule, MatSlideToggleModule,
        PageHeaderComponent,
    ],
    templateUrl: './userdata-type-attribute-dialog.component.html',
    styleUrl: './userdata-type-attribute-dialog.component.css',
})
export class UserDataTypeAttributeDialogComponent {

    private userDataTypeService: UserDataTypeService = inject(UserDataTypeService);
    private readOnlyStore: UserDataTypeReadOnlyStore = inject(UserDataTypeReadOnlyStore);
    private permissionChecker: PermissionCheckerService = inject(PermissionCheckerService);
    private dialogRef: MatDialogRef<UserDataTypeAttributeDialogComponent, UserDataTypeAttribute | null>
        = inject(MatDialogRef);

    /** The mat-select options stay in sync with UserDataValueType — if a new
     *  value type is added on the backend, add it here so the picker shows it. */
    readonly VALUE_TYPES: { value: UserDataValueType; label: string; icon: string }[] = [
        { value: 'string',  label: 'String',  icon: 'text_fields' },
        { value: 'integer', label: 'Integer', icon: 'numbers' },
        { value: 'semver',  label: 'SemVer',  icon: 'tag' },
    ];

    saving = false;
    errorMessage = '';
    form: FormGroup;
    /** True when editing — name + payloadPath become read-only. */
    isEdit = false;

    constructor(@Inject(MAT_DIALOG_DATA) public data: UserDataTypeAttributeDialogData) {
        const a = data.attribute;
        this.isEdit = !!a;
        // Read-only currently lives in localStorage (no backend column yet).
        // Prefer the persisted value over whatever the backend returns.
        const persistedReadOnly = a ? this.readOnlyStore.isReadOnly(data.type, a.name) : false;
        this.form = new FormGroup({
            name: new FormControl(
                { value: a?.name ?? '', disabled: this.isEdit },
                [Validators.required, Validators.maxLength(80)],
            ),
            payloadPath: new FormControl(
                { value: a?.payloadPath ?? '', disabled: this.isEdit },
                [Validators.required, Validators.maxLength(255)],
            ),
            valueType: new FormControl<UserDataValueType>(a?.valueType ?? 'string', [Validators.required]),
            indexed: new FormControl(a?.indexed ?? false),
            readOnly: new FormControl(persistedReadOnly || (a?.readOnly ?? false)),
            description: new FormControl(a?.description ?? ''),
        });
    }

    cancel(): void {
        this.dialogRef.close(null);
    }

    save(): void {
        if (this.form.invalid) return;
        // Both layers: the opener is gated, but guard the save too so a
        // programmatic submit can't mutate the type config without the cap.
        if (!this.permissionChecker.hasAccessToManageUserDataTypes()) { return; }
        this.saving = true;
        this.errorMessage = '';

        const v = this.form.getRawValue();
        const handler: ServiceHandler<UserDataTypeAttribute> = {
            onStart: () => {},
            onSuccess: (saved) => {
                // Persist readOnly to localStorage until the backend ships
                // the column. Use the saved attribute's name so it stays in
                // sync when create assigns a server-side identity.
                this.readOnlyStore.set(this.data.type, saved.name, !!v.readOnly);
                // Inject readOnly into the returned object so the parent
                // table refreshes with the new state without a re-fetch.
                this.dialogRef.close({ ...saved, readOnly: !!v.readOnly });
            },
            onError: (err) => { this.errorMessage = (err ?? 'Save failed').toString(); },
            onComplete: () => { this.saving = false; },
        };

        // NOTE: `readOnly` is intentionally NOT sent to the backend — the
        // userdata-service doesn't yet have a `read_only` column on
        // user_data_type_attribute and rejects the field with a 400. We
        // persist it locally via UserDataTypeReadOnlyStore in onSuccess
        // instead. Drop this comment + restore readOnly in the body once
        // the backend ships the column.
        if (this.isEdit && this.data.attribute) {
            // Echo name + payloadPath from the (immutable) attribute identity —
            // backend's UserDataTypeAttributeRequest record validates them as
            // @NotBlank even on PUT, so omitting them returns a 4xx/5xx.
            const body: UserDataTypeAttributeUpdateRequest = {
                name: this.data.attribute.name,
                payloadPath: this.data.attribute.payloadPath,
                valueType: v.valueType,
                indexed: v.indexed,
                description: (v.description ?? '').trim() || undefined,
                // Don't change source on edit — the dedicated lock/unlock action
                // owns flipping INFERRED ↔ MANUAL so accidental edits don't
                // promote inferred rules to manual ones.
            };
            this.userDataTypeService.updateAttribute(handler, this.data.type, this.data.attribute.name, body);
        } else {
            const body: UserDataTypeAttributeCreateRequest = {
                name: (v.name ?? '').trim(),
                payloadPath: (v.payloadPath ?? '').trim(),
                valueType: v.valueType,
                indexed: !!v.indexed,
                description: (v.description ?? '').trim() || undefined,
            };
            this.userDataTypeService.createAttribute(handler, this.data.type, body);
        }
    }
}
